import { useIntl } from "react-intl";

import { Divider } from "@/components/Divider/Divider";
import { Image } from "@/components/Image/Image";
import { RoomFacilityChip } from "@/features/hotels/components/hotel/RoomFacilityChip";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import type { HotelBookingRoomOption } from "@/features/hotels/types/types";

type Props = {
  hotelName: string;
  option: HotelBookingRoomOption;
};

export const RoomDetails = ({ hotelName, option }: Props) => {
  const intl = useIntl();

  const roomImage = option.images?.[0];
  const facilites = option.facilitiesDescription
    ?.split(",")
    .map((f) => f.trim());

  return (
    <div className="section-card flex flex-col gap-4">
      <div className="flex flex-col">
        <div className="text-xl font-medium text-text-primary">
          {option.name}
        </div>
        {hotelName ? (
          <div className="text-sm text-text-tertiary">
            <HotelName name={hotelName} />
          </div>
        ) : null}
      </div>

      <div className="flex aspect-[326/180] w-full items-center justify-center overflow-hidden rounded-lg bg-gray-100 text-center text-xs text-text-tertiary">
        <Image
          className="h-full w-full object-cover"
          {...(roomImage
            ? { src: roomImage.url, alt: roomImage.name }
            : {
                src: "/imagePlaceholder.svg",
                alt: intl.formatMessage({
                  defaultMessage: "No image available",
                  description:
                    "Fallback alt text shown when no hotel or room image is available",
                }),
              })}
        />
      </div>

      <Divider color="neutral-soft" />

      <div className="flex flex-col gap-2 text-sm text-text-primary">
        <span className="font-medium">
          {intl.formatMessage({
            defaultMessage: "Description",
            description: "Generic 'Description' text field label",
          })}
        </span>
        <span className="whitespace-pre-line">{option.description}</span>
      </div>

      {facilites && facilites.length > 0 ? (
        <div className="flex flex-col gap-2 text-sm text-text-primary">
          <span className="font-medium">
            {intl.formatMessage({
              defaultMessage: "Facilities",
              description: "Section heading for hotel facilities and amenities",
            })}
          </span>
          <div className="flex flex-wrap gap-2">
            {facilites?.map((facility) => (
              <RoomFacilityChip key={facility} label={facility} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
